import {
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  HELP_QUERY_PARAM,
  canOpenHelpGuide,
  getAvailableHelpGuideGroups,
  getAvailableHelpGuides,
  getHelpGuide,
  getHelpGuideForPath,
  type AvailableHelpGuideGroup,
  type HelpGuide,
  type HelpGuideId,
} from "../lib/helpGuides";
import { useEscapeKey } from "../lib/useEscapeKey";

export default function HelpLauncher({
  admin,
  contextualGuideId = null,
  disabled = false,
  hideTrigger = false,
  returnFocusRef,
}: {
  admin: boolean;
  contextualGuideId?: HelpGuideId | null;
  disabled?: boolean;
  /** Geöffnet wird dann über das Konto-Menü (#630); der Dialog bleibt hier. */
  hideTrigger?: boolean;
  returnFocusRef: RefObject<HTMLElement | null>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const routeGuide = useMemo(() => {
    const contextualGuide = getHelpGuide(contextualGuideId);
    const mappedGuide = getHelpGuideForPath(location.pathname, location.search);
    if (contextualGuide && canOpenHelpGuide(contextualGuide, admin)) {
      return contextualGuide;
    }
    if (canOpenHelpGuide(mappedGuide, admin)) return mappedGuide;
    return getAvailableHelpGuides(admin)[0];
  }, [admin, contextualGuideId, location.pathname]);
  const requestedGuideId = useMemo(
    () => new URLSearchParams(location.search).get(HELP_QUERY_PARAM),
    [location.search],
  );
  const requestedGuide = getHelpGuide(requestedGuideId);
  const selectedGuide =
    requestedGuide && canOpenHelpGuide(requestedGuide, admin)
      ? requestedGuide
      : routeGuide;
  const availableGuideGroups = useMemo(
    () => getAvailableHelpGuideGroups(admin),
    [admin],
  );
  const isOpen = requestedGuideId !== null;

  function setGuideInUrl(guideId: HelpGuideId, replace: boolean) {
    const params = new URLSearchParams(location.search);
    params.set(HELP_QUERY_PARAM, guideId);
    navigate(
      {
        pathname: location.pathname,
        search: searchString(params),
        hash: location.hash,
      },
      { replace },
    );
  }

  function openHelp() {
    if (disabled) return;
    returnFocusRef.current = triggerRef.current;
    setGuideInUrl(routeGuide.id, false);
  }

  function closeHelp() {
    const params = new URLSearchParams(location.search);
    params.delete(HELP_QUERY_PARAM);
    navigate(
      {
        pathname: location.pathname,
        search: searchString(params),
        hash: location.hash,
      },
      { replace: true },
    );
    const requestedTarget = returnFocusRef.current;
    returnFocusRef.current = null;
    window.requestAnimationFrame(() => {
      // Ohne eigenen Knopf (#630) geht der Fokus zurück an das Konto-Menü.
      const target =
        requestedTarget?.isConnected === true
          ? requestedTarget
          : triggerRef.current ?? document.querySelector<HTMLElement>("[data-account-menu-button]");
      target?.focus();
    });
  }

  return (
    <>
      {hideTrigger ? null : <button
        ref={triggerRef}
        type="button"
        onClick={openHelp}
        disabled={disabled}
        className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-300"
        aria-label={`Hilfe öffnen: ${routeGuide.listLabel}`}
        title="Hilfe"
      >
        <span aria-hidden="true">?</span>
      </button>}
      {isOpen ? (
        <HelpDialog
          guide={selectedGuide}
          groups={availableGuideGroups}
          onSelectGuide={(guideId) => setGuideInUrl(guideId, true)}
          onClose={closeHelp}
        />
      ) : null}
    </>
  );
}

function HelpDialog({
  guide,
  groups,
  onSelectGuide,
  onClose,
}: {
  guide: HelpGuide;
  groups: readonly AvailableHelpGuideGroup[];
  onSelectGuide: (guideId: HelpGuideId) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEscapeKey(onClose, true);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    function keepFocusInside(event: FocusEvent) {
      const dialog = dialogRef.current;
      if (dialog && !dialog.contains(event.target as Node)) {
        closeButtonRef.current?.focus();
      }
    }
    document.addEventListener("focusin", keepFocusInside);
    return () => {
      document.removeEventListener("focusin", keepFocusInside);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const dialog = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-3 sm:p-6"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-dialog-title"
        aria-describedby="help-dialog-purpose"
        tabIndex={-1}
        onKeyDown={(event) => trapDialogFocus(event, dialogRef)}
        onMouseDown={(event) => event.stopPropagation()}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:max-h-[calc(100dvh-3rem)]"
      >
        <header className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              So funktioniert TruuCost
            </p>
            <h2
              id="help-dialog-title"
              className="mt-1 text-lg font-semibold tracking-tight text-slate-950"
            >
              {guide.title}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Hilfe schließen"
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            ×
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,auto)_minmax(0,1fr)] md:grid-cols-[15rem_minmax(0,1fr)] md:grid-rows-1">
          <nav
            aria-label="Weitere Hilfen"
            data-testid="help-task-navigation"
            className="max-h-[40dvh] overflow-y-auto border-b border-slate-200 bg-slate-50 p-3 md:max-h-none md:border-b-0 md:border-r"
          >
            <h3
              id="help-tasks-heading"
              className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Aufgaben
            </h3>
            <div className="mt-3 space-y-4">
              {groups.map((group) => {
                const activeGroup = group.guides.some(
                  (candidate) => candidate.id === guide.id,
                );
                const headingId = `help-group-${group.id}`;
                return (
                  <section
                    key={group.id}
                    aria-labelledby={headingId}
                    data-help-group={group.id}
                    data-active={activeGroup ? "true" : "false"}
                    className={`border-l-2 pl-2 ${
                      activeGroup
                        ? "border-emerald-500"
                        : "border-transparent"
                    }`}
                  >
                    <h4
                      id={headingId}
                      className={`px-2 text-xs font-semibold uppercase tracking-wide ${
                        activeGroup ? "text-emerald-700" : "text-slate-500"
                      }`}
                    >
                      {group.label}
                    </h4>
                    <div className="mt-1 space-y-1">
                      {group.guides.map((candidate) => {
                        const selected = candidate.id === guide.id;
                        return (
                          <button
                            key={candidate.id}
                            type="button"
                            aria-current={selected ? "page" : undefined}
                            onClick={() => onSelectGuide(candidate.id)}
                            className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                              selected
                                ? "bg-white text-emerald-800 shadow-sm ring-1 ring-slate-200"
                                : "text-slate-600 hover:bg-white hover:text-slate-950"
                            }`}
                          >
                            {candidate.listLabel}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </nav>

          <article className="min-w-0 overflow-y-auto px-4 py-5 sm:px-6">
            <HelpGuideContent guide={guide} />
          </article>
        </div>

        <footer className="flex flex-none justify-end border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-md bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            Schließen
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

function HelpGuideContent({ guide }: { guide: HelpGuide }) {
  return (
    <div className="space-y-6 text-sm leading-6 text-slate-700">
      <section aria-labelledby="help-purpose-heading">
        <h3
          id="help-purpose-heading"
          className="font-semibold text-slate-950"
        >
          Wozu dient dieser Bereich?
        </h3>
        <p id="help-dialog-purpose" className="mt-1">
          {guide.purpose}
        </p>
      </section>

      <section aria-labelledby="help-steps-heading">
        <h3 id="help-steps-heading" className="font-semibold text-slate-950">
          So gehen Sie vor
        </h3>
        <ol className="mt-2 space-y-3">
          {guide.steps.map((step, index) => (
            <li key={step.title} className="flex gap-3">
              <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{step.title}</p>
                <p className="text-slate-600">{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="help-terms-heading">
        <h3 id="help-terms-heading" className="font-semibold text-slate-950">
          Wichtige Werte und Status
        </h3>
        <dl className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {guide.terms.map((entry) => (
            <div
              key={entry.term}
              className="grid gap-1 px-3 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3"
            >
              <dt className="font-medium text-slate-900">{entry.term}</dt>
              <dd className="text-slate-600">{entry.explanation}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="help-saving-heading">
        <h3 id="help-saving-heading" className="font-semibold text-slate-950">
          Was passiert beim Speichern?
        </h3>
        <p className="mt-1">{guide.saving}</p>
      </section>

      <section
        aria-labelledby="help-boundary-heading"
        className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
      >
        <h3 id="help-boundary-heading" className="font-semibold text-slate-950">
          Was erledigt TruuCost nicht?
        </h3>
        <p className="mt-1">{guide.boundary}</p>
      </section>

      <nav aria-label="Passende nächste Schritte" className="flex flex-wrap gap-2">
        {guide.links.map((link) => (
          <Link
            key={`${guide.id}:${link.to}`}
            to={link.to}
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            {link.label} →
          </Link>
        ))}
      </nav>
    </div>
  );
}

function searchString(params: URLSearchParams): string {
  const value = params.toString();
  return value ? `?${value}` : "";
}

function trapDialogFocus(
  event: KeyboardEvent<HTMLDivElement>,
  dialogRef: RefObject<HTMLDivElement | null>,
) {
  if (event.key !== "Tab") return;
  const dialog = dialogRef.current;
  if (!dialog) return;
  const focusable = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.getAttribute("aria-hidden") !== "true");
  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
